import { api, API } from "./_client";

export async function listWorkgroups() {
    const { data } = await api.get("/workgroups");
    return data;
}

export async function getWorkgroup(id) {
    const { data } = await api.get(`/workgroups/${id}`);
    return data;
}

export async function createWorkgroup(name) {
    const { data } = await api.post("/workgroups", { name });
    return data;
}

export async function updateWorkgroup(id, payload) {
    const { data } = await api.patch(`/workgroups/${id}`, payload);
    return data;
}

export async function deleteWorkgroup(id) {
    const { data } = await api.delete(`/workgroups/${id}`);
    return data;
}

export async function listWorkgroupMembers(id) {
    const { data } = await api.get(`/workgroups/${id}/members`);
    return data;
}

export async function updateWorkgroupMemberRole(wgId, memberId, role) {
    const { data } = await api.patch(
        `/workgroups/${wgId}/members/${memberId}`,
        { role },
    );
    return data;
}

export async function removeWorkgroupMember(wgId, memberId) {
    const { data } = await api.delete(`/workgroups/${wgId}/members/${memberId}`);
    return data;
}

export async function sendWorkgroupInvite(wgId, email) {
    const { data } = await api.post(`/workgroups/${wgId}/invites`, { email });
    return data;
}

export async function listWorkgroupInvites(wgId) {
    const { data } = await api.get(`/workgroups/${wgId}/invites`);
    return data;
}

export async function cancelWorkgroupInvite(wgId, inviteId) {
    const { data } = await api.delete(`/workgroups/${wgId}/invites/${inviteId}`);
    return data;
}

export async function listMyInvites() {
    const { data } = await api.get("/invites");
    return data;
}

export async function acceptInvite(inviteId) {
    const { data } = await api.post(`/invites/${inviteId}/accept`);
    return data;
}

export async function declineInvite(inviteId) {
    const { data } = await api.post(`/invites/${inviteId}/decline`);
    return data;
}

export async function listNotifications() {
    const { data } = await api.get("/notifications");
    return data;
}

export async function getUnreadNotificationCount() {
    const { data } = await api.get("/notifications/unread-count");
    return data.count || 0;
}

export async function markNotificationRead(id) {
    const { data } = await api.post(`/notifications/${id}/read`);
    return data;
}

export async function markAllNotificationsRead() {
    const { data } = await api.post("/notifications/read-all");
    return data;
}

export async function getActiveWorkgroup() {
    const { data } = await api.get("/workgroups/active/current");
    return data.workgroup;
}

export async function setActiveWorkgroup(workgroupId) {
    const { data } = await api.post("/workgroups/active", {
        workgroup_id: workgroupId,
    });
    return data;
}

export async function saveToWorkgroup(
    kind,
    itemId,
    workgroupId,
    mode = "copy",
    { deepCopy = false } = {},
) {
    const { data } = await api.post(`/workgroups/save/${kind}/${itemId}`, {
        workgroup_id: workgroupId,
        mode,
        deep_copy: deepCopy,
    });
    return data;
}

export async function getWorkgroupDashboard(wgId, { onlySharedWithMe = false } = {}) {
    const params = {};
    if (onlySharedWithMe) params.only_shared_with_me = true;
    const { data } = await api.get(`/workgroups/${wgId}/dashboard`, { params });
    return data;
}

// iter-55: unshare a row from a workgroup (keeps it in the sharer's
// private list, just detaches it from the wg scope). Allowed for the
// wg admin OR the original sharer.

export async function unshareFromWorkgroup(wgId, kind, itemId) {
    const { data } = await api.post(
        `/workgroups/${wgId}/shared/${kind}/${itemId}/unshare`,
    );
    return data;
}

// Hard delete a workgroup-scoped row (wg admin or original sharer).

export async function deleteWorkgroupShare(wgId, kind, itemId) {
    const { data } = await api.delete(
        `/workgroups/${wgId}/shared/${kind}/${itemId}`,
    );
    return data;
}

// iter-57: pull a workgroup-scoped row into the caller's personal list.
// For projects this cascades uploads/analyses/estimates/takeoffs so the
// personal copy is immediately usable without re-running AI.

export async function copyWorkgroupShareToPersonal(wgId, kind, itemId) {
    const { data } = await api.post(
        `/workgroups/${wgId}/shared/${kind}/${itemId}/copy-to-personal`,
    );
    return data;
}

// Re-sync a workgroup-scoped row from the original sharer's personal
// copy. Only the sharer (or a workgroup admin) can call. Requires that
// the share was created via save_to_workgroup AFTER iter-57 (older
// shares will 400 — delete + re-share to opt in).

export async function refreshWorkgroupShare(wgId, kind, itemId) {
    const { data } = await api.post(
        `/workgroups/${wgId}/shared/${kind}/${itemId}/refresh`,
    );
    return data;
}

// Trigger a browser "Save As" of every uploaded plan in a shared
// project as a single zip. Only supported for kind="project".

export async function downloadWorkgroupShare(wgId, kind, itemId, fileName = "plans") {
    const res = await api.get(
        `/workgroups/${wgId}/shared/${kind}/${itemId}/download`,
        { responseType: "blob" },
    );
    const blob = new Blob([res.data], { type: "application/zip" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(fileName || "plans").replace(/\s+/g, "_")}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ----- Workgroup chat (iter-60) -----

export async function listWorkgroupMessages(wgId, { before, limit = 50 } = {}) {
    const params = { limit };
    if (before) params.before = before;
    const { data } = await api.get(`/workgroups/${wgId}/messages`, { params });
    return data;
}

export async function sendWorkgroupMessage(wgId, payload) {
    // Backwards-compat: if the caller passed a plain string, treat it
    // as the body. Callers that send attachments pass an object.
    const body =
        typeof payload === "string" ? { body: payload } : payload;
    const { data } = await api.post(`/workgroups/${wgId}/messages`, body);
    return data;
}

export async function uploadWorkgroupAttachment(wgId, file, onProgress) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post(
        `/workgroups/${wgId}/messages/attachments`,
        form,
        {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        },
    );
    return data;
}

// Build a GET URL that streams the attachment with auth via ?token=.
// Used as the `src=` for <img>, <video>, <audio> tags (which can't
// carry an Authorization header).

export function workgroupAttachmentSrc(attachmentUrl) {
    if (!attachmentUrl) return "";
    const token =
        typeof localStorage !== "undefined"
            ? localStorage.getItem("planflow.auth_token") || ""
            : "";
    const origin = API.replace(/\/api$/, "");
    const sep = attachmentUrl.includes("?") ? "&" : "?";
    return `${origin}${attachmentUrl}${sep}token=${encodeURIComponent(token)}`;
}

export async function markChatSeen(wgId) {
    const { data } = await api.post(`/workgroups/${wgId}/messages/mark-seen`);
    return data;
}

// -------- Subscriptions (Phase 1: admin) --------
